import React, { useState, useEffect, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./InstagramFeed.css";

const CACHE_KEY = 'instagramPosts';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em milissegundos
const REFRESH_INTERVAL = 5 * 60 * 1000; // Atualiza a cada 5 minutos

// URL do Worker Cloudflare (token seguro no servidor)
const WORKER_URL = 'https://ig-proxy-site.bielsilvasantos3.workers.dev/ig';

const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const fetchInstagramPosts = useCallback(async (forceRefresh = false) => {
    try {
      // Verificar cache local (evita muitas requisições)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const isValid = Date.now() - timestamp < CACHE_DURATION;
          if (isValid && data.length > 0) {
            setPosts(data);
            setLoading(false);
            return;
          }
        }
      }

      // Busca via Worker Cloudflare (token protegido no servidor)
      const response = await fetch(`${WORKER_URL}?limit=6`);

      if (!response.ok) {
        throw new Error("Erro ao carregar posts");
      }

      const data = await response.json();
      const postsData = data.data || [];
      
      // Salvar no cache
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: postsData,
        timestamp: Date.now()
      }));
      
      setPosts(postsData);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Erro ao buscar posts do Instagram:", err);
      
      // Se falhar, tentar usar cache antigo
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        if (data.length > 0) {
          setPosts(data);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Buscar posts na montagem
    fetchInstagramPosts();

    // Configurar refresh automático a cada 5 minutos
    const interval = setInterval(() => {
      fetchInstagramPosts(true);
    }, REFRESH_INTERVAL);

    // Também atualizar quando a aba ficar visível novamente
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchInstagramPosts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchInstagramPosts]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // Posts placeholder quando não há token configurado
  const placeholderPosts = [
    { id: 1, type: "placeholder" },
    { id: 2, type: "placeholder" },
    { id: 3, type: "placeholder" },
    { id: 4, type: "placeholder" },
    { id: 5, type: "placeholder" },
    { id: 6, type: "placeholder" },
  ];

  const displayPosts = posts.length > 0 ? posts : placeholderPosts;
  const isPlaceholder = posts.length === 0;

  return (
    <section id="instagram" className="instagram-section" ref={ref}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={controls}
          variants={{
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5"
        >
          <h2 className="instagram-title">Siga-nos no Instagram</h2>
          <p className="instagram-subtitle">
            Acompanhe nossos momentos e fique por dentro das novidades
          </p>
        </motion.div>

        {loading ? (
          <div className="instagram-loading">
            <div className="spinner"></div>
            <p>Carregando posts...</p>
          </div>
        ) : error && !isPlaceholder ? (
          <div className="instagram-error">
            <p>Não foi possível carregar os posts. Tente novamente mais tarde.</p>
          </div>
        ) : (
          <motion.div
            className="instagram-grid"
            variants={containerVariants}
            initial="hidden"
            animate={controls}
          >
            {displayPosts.map((post, index) => (
              <motion.div
                key={post.id}
                className="instagram-item"
                variants={itemVariants}
              >
                {isPlaceholder ? (
                  <div className="instagram-placeholder">
                    <div className="placeholder-icon">
                      <i className="bi bi-instagram"></i>
                    </div>
                    <p>Em breve</p>
                  </div>
                ) : (
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="instagram-link"
                  >
                    <div className="instagram-image-wrapper">
                      {post.media_type === "VIDEO" ? (
                        <img
                          src={post.thumbnail_url || post.media_url}
                          alt={post.caption?.slice(0, 50) || "Post do Instagram"}
                          className="instagram-image"
                        />
                      ) : (
                        <img
                          src={post.media_url}
                          alt={post.caption?.slice(0, 50) || "Post do Instagram"}
                          className="instagram-image"
                        />
                      )}
                      {post.media_type === "VIDEO" && (
                        <div className="video-indicator">
                          <i className="bi bi-play-circle-fill"></i>
                        </div>
                      )}
                      <div className="instagram-overlay">
                        <i className="bi bi-instagram"></i>
                        <span>Ver no Instagram</span>
                      </div>
                    </div>
                    {post.caption && (
                      <p className="instagram-caption">
                        {post.caption.length > 80
                          ? `${post.caption.slice(0, 80)}...`
                          : post.caption}
                      </p>
                    )}
                  </a>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        <motion.div
          className="instagram-cta"
          initial={{ opacity: 0 }}
          animate={controls}
          variants={{
            visible: { opacity: 1 },
          }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <a
            href="https://www.instagram.com/casadeoracaohengelo/"
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-button"
          >
            <i className="bi bi-instagram"></i>
            @casadeoracaohengelo
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default InstagramFeed;
